import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Rocket,
  Building2,
  Users,
  Sparkles,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Dumbbell,
  Brain,
  Apple,
  SmilePlus,
  LayoutGrid,
} from "lucide-react";
import { Button, Input, Label } from "@/components/ui";
import { useToast } from "@/hooks";
import {
  fetchOnboardingStatus,
  updateOnboardingStep,
  completeOnboarding,
  fetchOnboardingTemplates,
  ONBOARDING_QUERY_KEY,
  type OnboardingStatus,
  type OnboardingTemplates,
  type ClinicType,
} from "@/services/onboarding";

const CLINIC_TYPES: { value: ClinicType; label: string; icon: React.ElementType; gradient: string }[] = [
  { value: "estetica", label: "Estética", icon: Sparkles, gradient: "bg-brand" },
  { value: "fisioterapia", label: "Fisioterapia", icon: Dumbbell, gradient: "bg-app-info" },
  { value: "psicologia", label: "Psicologia", icon: Brain, gradient: "bg-brand" },
  { value: "nutricao", label: "Nutrição", icon: Apple, gradient: "bg-app-success" },
  { value: "odontologia", label: "Odontologia", icon: SmilePlus, gradient: "bg-brand" },
  { value: "geral", label: "Clínica Geral", icon: LayoutGrid, gradient: "bg-app-fg-muted" },
];

const STEPS = ["clinic_type", "team", "templates", "complete"];

export function OnboardingWizard() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [templates, setTemplates] = useState<OnboardingTemplates | null>(null);
  const [loading, setLoading] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [templatesError, setTemplatesError] = useState(false);
  const [templateAttempt, setTemplateAttempt] = useState(0);

  // Form state
  const [clinicType, setClinicType] = useState<ClinicType>("estetica");
  const [specialty, setSpecialty] = useState("");
  const [teamSize, setTeamSize] = useState("3");

  async function load() {
    setLoading(true);
    const s = await fetchOnboardingStatus();
    setStatus(s);
    if (s) {
      setClinicType(s.clinic_type || "estetica");
      setSpecialty(s.specialty || "");
      setTeamSize(String(s.team_size || 3));
      const idx = STEPS.indexOf(s.current_step);
      if (idx >= 0) setStepIndex(Math.min(idx, 2));
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    let active = true;
    if (stepIndex === 2) {
      setTemplates(null);
      setTemplatesError(false);
      fetchOnboardingTemplates(clinicType).then((result) => {
        if (!active) return;
        setTemplates(result);
        setTemplatesError(!result);
      });
    }
    return () => { active = false; };
  }, [stepIndex, clinicType, templateAttempt]);

  async function handleNext() {
    const nextStep = STEPS[stepIndex + 1];
    if (!nextStep) return;

    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await updateOnboardingStep({
        current_step: nextStep,
        completed_steps: STEPS.slice(0, stepIndex + 1),
        clinic_type: clinicType,
        specialty: specialty || undefined,
        team_size: Number(teamSize) || 3,
      });
      if (!saved) {
        setSaveError("Não foi possível salvar esta etapa. Seus dados foram mantidos. Tente novamente em Próximo.");
        return;
      }
      setStatus(saved);
      setStepIndex(stepIndex + 1);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  function handleBack() {
    if (stepIndex <= 0 || savingRef.current) return;
    setSaveError(null);
    setStepIndex(stepIndex - 1);
  }

  async function handleComplete() {
    if (savingRef.current || !templates) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      const completed = await completeOnboarding();
      if (!completed?.is_completed) {
        setSaveError("Não foi possível concluir a configuração. Tente novamente.");
        return;
      }
      queryClient.setQueryData(ONBOARDING_QUERY_KEY, completed);
      setStatus(completed);
      toast({ title: "Configuração inicial concluída" });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div role="status" aria-label="Carregando configuração da clínica" className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  if (!status) {
    return <div className="mx-auto max-w-md space-y-4 py-12">
      <p role="alert" className="text-app-fg">Não foi possível carregar a configuração da clínica.</p>
      <Button variant="outline" onClick={() => void load()}>Tentar novamente</Button>
    </div>;
  }

  if (status.is_completed) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-app-success flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Configuração inicial concluída</h1>
          <p className="text-muted-foreground mb-6">
            Revise os procedimentos e as orientações da Equipe de IA.
            O atendimento automático precisa ser ativado e confirmado separadamente.
          </p>
          <div className="flex flex-col justify-center gap-2 sm:flex-row">
            <Button
              className="gap-2 bg-brand text-white hover:bg-brand"
              onClick={() => navigate("/growth-team")}
            >
              <Sparkles className="w-4 h-4" />
              Configurar Equipe de IA
            </Button>
            <Button variant="outline" onClick={() => navigate("/")}>
              Ir para o painel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Progress bar */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.slice(0, -1).map((step, i) => (
            <div key={step} className="flex items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${i <= stepIndex ? "bg-brand text-white" : "bg-muted text-muted-foreground"}`}>
                {i < stepIndex ? <CheckCircle className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 2 && (
                <div className={`flex-1 h-1 mx-2 rounded-full transition-colors ${i < stepIndex ? "bg-brand" : "bg-muted"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Clinic Type */}
        {stepIndex === 0 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <Rocket className="w-12 h-12 text-brand mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-foreground">Qual é o tipo da sua clínica?</h2>
              <p className="text-muted-foreground mt-1">Vamos personalizar tudo para o seu segmento</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {CLINIC_TYPES.map((ct) => {
                const Icon = ct.icon;
                const selected = clinicType === ct.value;
                return (
                  <button
                    key={ct.value}
                    disabled={saving}
                    aria-pressed={selected}
                    onClick={() => setClinicType(ct.value)}
                    className={`p-4 rounded-xl border-2 transition-all text-center ${selected ? "border-brand bg-brand-soft shadow-md scale-[1.02]" : "border-border hover:border-brand hover:bg-muted/50"}`}
                  >
                    <div className={`w-12 h-12 rounded-xl ${ct.gradient} text-white flex items-center justify-center mx-auto mb-2`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <p className={`text-sm font-semibold ${selected ? "text-brand-hover" : "text-foreground"}`}>{ct.label}</p>
                  </button>
                );
              })}
            </div>
            <div className="mt-4">
              <Label htmlFor="onboarding-specialty" className="text-sm">Especialidade (opcional)</Label>
              <Input
                id="onboarding-specialty"
                disabled={saving}
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                placeholder="Ex: Harmonização Facial, Pilates Clínico..."
                className="mt-1"
              />
            </div>
          </div>
        )}

        {/* Step 2: Team */}
        {stepIndex === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <Users className="w-12 h-12 text-app-info mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-foreground">Quantas pessoas na equipe?</h2>
              <p className="text-muted-foreground mt-1">Isso ajuda a configurar permissões e agendas</p>
            </div>
            <div className="grid grid-cols-4 gap-3 max-w-md mx-auto">
              {[1, 3, 5, 10, 15, 20, 30, 50].map((n) => (
                <button
                  key={n}
                  disabled={saving}
                  aria-pressed={Number(teamSize) === n}
                  onClick={() => setTeamSize(String(n))}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${Number(teamSize) === n ? "border-app-info bg-app-info/10" : "border-border hover:border-app-info"}`}
                >
                  <p className="text-lg font-bold text-foreground">{n}</p>
                  <p className="text-[10px] text-muted-foreground">{n === 1 ? "Só eu" : n <= 5 ? "Pequena" : n <= 15 ? "Média" : "Grande"}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Clinical base preview */}
        {stepIndex === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <Building2 className="w-12 h-12 text-brand mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-foreground">
                Base inicial da sua clínica
              </h2>
              <p className="text-muted-foreground mt-1">
                Uma referência para começar em{" "}
                {CLINIC_TYPES.find((clinic) => clinic.value === clinicType)?.label}.
                Você continua no controle de todos os cadastros.
              </p>
            </div>
            {templates ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-border bg-card p-4">
                  <h3 className="mb-1 text-sm font-semibold text-foreground">
                    Procedimentos sugeridos
                  </h3>
                  <p className="mb-3 text-xs text-muted-foreground">
                    São sugestões, não cadastros automáticos. Revise nomes,
                    duração e preços no módulo de Procedimentos.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {templates.procedures.map((p, i) => (
                      <span key={i} className="px-3 py-1 rounded-full bg-brand-soft text-brand-hover text-xs font-medium border border-brand-soft">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-4">
                  <h3 className="mb-1 text-sm font-semibold text-foreground">
                    Equipe de IA
                  </h3>
                  <p className="mb-3 text-xs text-muted-foreground">
                    Especialistas digitais que colaboram entre si e com a equipe
                    humana da clínica.
                  </p>
                  <div className="space-y-2">
                    {templates.growth_team_roles.map((role) => (
                      <div
                        className="flex items-start gap-3 rounded-lg bg-muted/50 p-3 text-sm"
                        key={role.type}
                      >
                        <Sparkles className="mt-0.5 w-3.5 shrink-0 text-brand" />
                        <div>
                          <p className="font-medium text-foreground">
                            {role.label}
                          </p>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {role.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-2">
                    Saudação sugerida
                  </h3>
                  <p className="text-sm text-muted-foreground bg-muted rounded-lg p-3">
                    {templates.greeting_prompt}
                  </p>
                </div>
              </div>
            ) : templatesError ? (
              <div className="space-y-3">
                <p role="alert" className="text-app-danger">Não foi possível carregar as sugestões. Tente novamente antes de concluir.</p>
                <Button variant="outline" onClick={() => setTemplateAttempt((value) => value + 1)}>Tentar novamente</Button>
              </div>
            ) : (
              <div role="status" aria-label="Carregando sugestões" className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand border-t-transparent" />
              </div>
            )}
          </div>
        )}

        {saveError && <p role="alert" className="mt-4 text-sm text-app-danger">{saveError}</p>}
        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button variant="outline" onClick={handleBack} disabled={stepIndex === 0 || saving} className="gap-1">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Button>
          {stepIndex < 2 ? (
            <Button disabled={saving} onClick={handleNext} className="gap-1 bg-brand hover:bg-brand text-white">
              {saving ? "Salvando…" : "Próximo"} <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button disabled={saving || !templates} onClick={handleComplete} className="gap-1 bg-app-success hover:bg-app-success text-white">
              <CheckCircle className="w-4 h-4" /> {saving ? "Salvando…" : "Concluir configuração"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
